import { useState, useCallback } from "react";
import {
  usePaginatedResource,
  PaginatedFetcher,
  pageFrom,
} from "./usePaginatedResource";
import { useEntityOperations } from "./useEntityOperations";
import { useEntity } from "./useEntity";
import { useEntitySearch, SearchPage } from "./useEntitySearch";
import { ProgramService } from "../services/programService";
import {
  TrainingProgram,
  CreateProgramInput,
  UpdateProgramInput,
  ProgramFilters,
} from "../types/program";

// Hook for managing programs list with pagination and filtering
export const usePrograms = (
  initialPage: number = 1,
  initialLimit: number = 30,
  initialFilters: ProgramFilters = {}
) => {
  const fetcher = useCallback<
    PaginatedFetcher<TrainingProgram, ProgramFilters>
  >(async (page, filters) => {
    const response = await ProgramService.getPrograms(page, filters);
    return response ? pageFrom(response.programs, response) : null;
  }, []);

  const { items, ...rest } = usePaginatedResource<
    TrainingProgram,
    ProgramFilters
  >(fetcher, {
    initialPage,
    initialLimit,
    initialFilters,
    errorMessage: "Failed to fetch programs",
  });

  // allPrograms was always assigned the same page as `programs`; kept as an
  // alias so existing consumers are unaffected.
  return { programs: items, allPrograms: items, ...rest };
};

// Hook for managing individual program operations
export const useProgramOperations = () => {
  const { run, runBoolean, ...rest } = useEntityOperations();

  const createProgram = useCallback(
    (programData: CreateProgramInput) =>
      run(
        () => ProgramService.createProgram(programData),
        "Failed to create program"
      ),
    [run]
  );

  const updateProgram = useCallback(
    (programId: number, updates: UpdateProgramInput) =>
      run(
        () => ProgramService.updateProgram(programId, updates),
        "Failed to update program"
      ),
    [run]
  );

  const deleteProgram = useCallback(
    (programId: number) =>
      runBoolean(
        () => ProgramService.deleteProgram(programId),
        "Failed to delete program"
      ),
    [runBoolean]
  );

  return { createProgram, updateProgram, deleteProgram, ...rest };
};

// Hook for getting individual program data
export const useProgram = (programId: number | null) => {
  const fetcher = useCallback(
    (id: number) => ProgramService.getProgramById(id),
    []
  );

  const { data, setData, ...rest } = useEntity<TrainingProgram>(
    programId,
    fetcher,
    {
      notFoundMessage: "Program not found",
      errorMessage: "An unexpected error occurred while fetching program",
    }
  );

  return { program: data, setProgram: setData, ...rest };
};

// Hook for searching programs with infinite scroll support
export const useProgramSearch = () => {
  const fetcher = useCallback(
    async (
      searchTerm: string,
      page: number,
      limit: number,
      challengeId?: number
    ): Promise<SearchPage<TrainingProgram> | null> => {
      const response = await ProgramService.searchPrograms(
        searchTerm,
        page,
        limit,
        challengeId
      );
      if (!response?.data) return null;

      return {
        items: response.data.data,
        total: response.data.total,
        page: response.data.current_page,
        lastPage: response.data.last_page,
      };
    },
    []
  );

  const { search, ...rest } = useEntitySearch<TrainingProgram, number>(
    fetcher,
    {
      limit: 30,
      errorMessage: "Failed to search programs",
    }
  );

  return { search, searchPrograms: search, ...rest };
};
